import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import keys from "lodash/keys";

import { ssoGroupsMappingText } from "../../../../assets/texts/Settings/sso";
import { BottomAppBar } from "../../../../Components/BottomAppBar";
import { DraggableContainer } from "../../../../Components/DragAndDrop/DraggableContainer";
import { DraggableGroup } from "../../../../Components/DragAndDrop/DraggableGroup";
import { useRoles } from "../../../../Components/hooks/IAM/useRoles";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { getDefaultRoleRefByCustomer } from "../../../../utils/permissions";
import { useSsoGroupMapping } from "./hooks/useSsoGroupMapping";
import SsoAddMappingButton from "./SsoAddMappingButton";
import SsoGroupMappingHeaders from "./SsoGroupMappingHeaders";
import SsoGroupMappingItem from "./SsoGroupMappingItem";

export const SSO_VALIDATION_REGEX = /^[a-zA-Z0-9_-]+$/;

export type GroupApiMapping = {
  groupId: string;
  roleAtDoit: string;
  org: string;
};

export type GroupMapping = {
  groupId: string;
  role: string;
  organization: string;
};

export type SsoGroupMappingListProps = {
  ssoEnabled?: boolean;
};

export type IdNameMapping = {
  id: string;
  name: string;
};

export const cyIds = {
  list: "ssoGroupMappingList",
  attributeKey: "ssoGroupMappingAttributeKey",
  item: (id: string): string => `ssoGroupMappingItem-${id}`,
  bottomAppBar: "sabmit-sso-group-mappings",
  buttons: {
    add: "ssoGroupMappingAddButton",
    cancel: "ssoGroupMappingCancelButton",
    save: "ssoGroupMappingSaveButton",
  },
  headers: "ssoGroupMappingHeaders",
};

const SsoGroupMappingList = ({ ssoEnabled = true }: SsoGroupMappingListProps) => {
  const { roles } = useRoles();
  const { organizations, customer } = useCustomerContext();
  const [attributeKey, setAttributeKey] = useState<string>("");

  // Track attribute key validation - matching group ID validation pattern
  const [attributeKeyError, setAttributeKeyError] = useState<boolean>(false);
  const [attributeKeyHelperText, setAttributeKeyHelperText] = useState<string | undefined>(undefined);
  const [hasStartedTypingAttributeKey, setHasStartedTypingAttributeKey] = useState<boolean>(false);

  const defaultRoleId = getDefaultRoleRefByCustomer(customer)?.id;

  const {
    isContentUpdated,
    userGroups,
    items,
    loading,
    error,
    saving,
    defaultMapping,
    handleItemsOrder,
    handleMappingChange,
    handleDelete,
    handleAddMapping,
    handleSaveChanges,
    handleReset,
  } = useSsoGroupMapping(customer.id, attributeKey, setAttributeKey);

  /**
   * Attribute key validation logic - matching the group ID validation pattern exactly
   */
  useEffect(() => {
    const isAttributeKeyEmpty = attributeKey === "";
    const isAttributeKeyValid = attributeKey === "" || SSO_VALIDATION_REGEX.test(attributeKey);

    // Show error if user has started typing and field is empty, or if format validation fails
    const hasError =
      ssoEnabled &&
      ((hasStartedTypingAttributeKey && isAttributeKeyEmpty && !!keys(items.main).length) || !isAttributeKeyValid);
    setAttributeKeyError(hasError);

    if (!hasError) {
      setAttributeKeyHelperText(undefined);
      return;
    }

    let helperText = "";
    if (hasStartedTypingAttributeKey && isAttributeKeyEmpty) {
      helperText = "Attribute key is required";
    } else if (!isAttributeKeyValid) {
      helperText = "Attribute key can only contain letters, numbers, underscores, and hyphens";
    }
    setAttributeKeyHelperText(helperText);
  }, [ssoEnabled, attributeKey, hasStartedTypingAttributeKey, items]);

  const rolesList = useMemo<IdNameMapping[]>(
    () =>
      roles
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((role) => ({
          id: role.id,
          name: role.id === defaultRoleId ? `${role.name} (Default)` : role.name,
        })),
    [defaultRoleId, roles]
  );

  const orgsList = useMemo<IdNameMapping[]>(
    () =>
      organizations
        .sort((a, b) => a.data.name.localeCompare(b.data.name, undefined, { sensitivity: "base" }))
        .map((org) => ({
          id: org.ref.id,
          name: org.data.name,
        })),
    [organizations]
  );

  const showOrganizationColumn = useMemo<boolean>(() => orgsList.length > 1, [orgsList.length]);

  // Track validation errors for each item
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  const handleValidationChange = useCallback((id: string, hasError: boolean) => {
    setValidationErrors((prev) => ({
      ...prev,
      [id]: hasError,
    }));
  }, []);

  const hasValidationErrors = useMemo(
    () => Object.values(validationErrors).some((hasError) => hasError) || attributeKeyError,
    [validationErrors, attributeKeyError]
  );

  const existingGroupIdsWithoutCurrent = useCallback<(rowId: string) => string[]>(
    (rowId: string) =>
      Object.entries(userGroups)
        .filter(([id, group]) => id !== rowId && group.groupId !== "" && group.groupId !== defaultMapping.groupId)
        .map(([_, group]) => group.groupId),
    [userGroups, defaultMapping.groupId]
  );

  const handleAttributeKeyChange = (value: string) => {
    setHasStartedTypingAttributeKey(true);
    setAttributeKey(value);
  };

  // Update the existing handleReset to also reset attribute key validation
  const handleResetWithAttributeKey = () => {
    handleReset();
    setHasStartedTypingAttributeKey(false);
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Typography color="error">Failed to load SSO group mappings: {error}</Typography>;
  }

  return (
    <Stack spacing={1}>
      <fieldset
        disabled={!ssoEnabled}
        style={{
          border: 0,
          padding: 0,
          margin: 0,
          minWidth: 0,
          opacity: ssoEnabled ? 1 : 0.5,
          pointerEvents: ssoEnabled ? "auto" : "none",
        }}
      >
        <Typography variant="h3">{ssoGroupsMappingText.GROUPS_MAPPING_HEADER}</Typography>
        <Stack spacing={3}>
          <Typography variant="body1" color="textSecondary">
            {ssoGroupsMappingText.GROUPS_MAPPING_DESCRIPTION}
          </Typography>
          <Stack spacing={0}>
            <TextField
              fullWidth
              label={ssoGroupsMappingText.GROUPS_ATTR_KEY_LABEL}
              value={attributeKey}
              size="small"
              data-cy={cyIds.attributeKey}
              onChange={(e) => {
                handleAttributeKeyChange(e.target.value);
              }}
              error={attributeKeyError}
              helperText={attributeKeyHelperText}
            />
            <Typography variant="caption" color="textSecondary">
              {ssoGroupsMappingText.GROUPS_ATTR_KEY_DESCRIPTION}
            </Typography>
          </Stack>
          <Stack spacing={2} data-cy={cyIds.list}>
            <DraggableContainer
              values={items}
              onItemsOrderChanged={handleItemsOrder}
              mouseSensorOptions={{ activationConstraint: { delay: 100, tolerance: 5 } }}
              renderDraggingItem={(id) => (
                <SsoGroupMappingItem
                  id={id}
                  rolesList={rolesList}
                  orgsList={orgsList}
                  grabbing
                  {...userGroups[id]}
                  onDelete={() => {
                    handleDelete(id);
                  }}
                  onChange={handleMappingChange}
                  showOrganizationColumn={showOrganizationColumn}
                  disabled={!ssoEnabled}
                  existingGroupIds={existingGroupIdsWithoutCurrent(id)}
                  onValidationChange={handleValidationChange}
                />
              )}
            >
              <Stack direction="column" sx={{ width: "100%" }}>
                <SsoAddMappingButton onAdd={handleAddMapping} disabled={!ssoEnabled} />
                <SsoGroupMappingHeaders showOrganizationColumn={showOrganizationColumn} />
                <DraggableGroup
                  id="main"
                  renderContent={(items) => (
                    <Box>
                      {items.map((id) => (
                        <SsoGroupMappingItem
                          key={id}
                          id={id}
                          rolesList={rolesList}
                          orgsList={orgsList}
                          {...userGroups[id]}
                          onDelete={() => {
                            handleDelete(id);
                          }}
                          onChange={handleMappingChange}
                          showOrganizationColumn={showOrganizationColumn}
                          disabled={!ssoEnabled}
                          existingGroupIds={existingGroupIdsWithoutCurrent(id)}
                          onValidationChange={handleValidationChange}
                        />
                      ))}
                    </Box>
                  )}
                />
                <SsoGroupMappingItem
                  key={ssoGroupsMappingText.ROLE_ID_DEFAULT}
                  id={ssoGroupsMappingText.ROLE_ID_DEFAULT}
                  rolesList={rolesList}
                  orgsList={orgsList}
                  {...defaultMapping}
                  onDelete={() => {}}
                  onChange={() => {}}
                  showOrganizationColumn={showOrganizationColumn}
                  disabled={true}
                  onValidationChange={handleValidationChange}
                />
              </Stack>
            </DraggableContainer>
            <BottomAppBar
              maxWidth="md"
              primaryButtonName={saving ? ssoGroupsMappingText.BUTTON_SAVING : ssoGroupsMappingText.BUTTON_SAVE}
              primaryButtonProps={{ "data-cy": cyIds.buttons.save }}
              handlePrimaryButtonClicked={handleSaveChanges}
              secondaryButtonName={ssoGroupsMappingText.BUTTON_CANCEL}
              secondaryButtonProps={{ "data-cy": cyIds.buttons.cancel }}
              handleSecondaryButtonClicked={handleResetWithAttributeKey}
              disabled={saving || !isContentUpdated || !ssoEnabled || hasValidationErrors}
              data-cy={cyIds.bottomAppBar}
            />
          </Stack>
        </Stack>
      </fieldset>
    </Stack>
  );
};

export default SsoGroupMappingList;
